import type { AgentLead } from '@/shared/api/agentApi';

/**
 * דדופ תוצאות חיפוש לאדם אחד (אפיון 5.6): אותו אדם יכול להיות ליד בכמה
 * קמפיינים, והנציג צריך לראות אותו שורה אחת — עם רשימת הקמפיינים שבהם הוא
 * נמצא, לא שורה חוזרת לכל אחד.
 *
 * זהות האדם היא contact_id כשהוא קיים (זה מזהה האדם בשרת); כשאין — נופלים
 * למספר הטלפון המנורמל, כי ליד מיובא בלי contact_id עדיין מזוהה בטלפון.
 * הליד הנבחר לייצוג הוא הראשון ברשימה, כלומר סדר השרת נשמר.
 */
export function leadsByPerson(leads: AgentLead[]): PersonResult[] {
  const byKey = new Map<string, PersonResult>();

  for (const lead of leads) {
    const key = lead.contact_id != null ? `c:${lead.contact_id}` : `p:${normalizePhone(lead.phone)}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.leads.push(lead);
      // שם חסר בליד הראשון עדיין יכול להופיע בליד אחר של אותו אדם.
      if (!existing.name && lead.name) existing.name = lead.name;
      continue;
    }
    byKey.set(key, { key, phone: lead.phone, name: lead.name ?? null, leads: [lead] });
  }

  return [...byKey.values()];
}

/** אדם אחד בתוצאות החיפוש, עם כל הלידים שלו על פני הקמפיינים. */
export interface PersonResult {
  key: string;
  phone: string;
  name: string | null;
  /** כל הלידים של האדם. הראשון הוא הליד המייצג (סדר השרת). */
  leads: AgentLead[];
}

/**
 * השוואת מספרי טלפון לצורך זיהוי אדם: מסירים כל מה שאינו ספרה ומיישרים
 * קידומת ישראלית (972…‎ ↔ 0…‎) כדי ש-0501234567 ו-+972-50-123-4567 יזוהו
 * כאותו אדם.
 */
export function normalizePhone(phone: string): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (digits.startsWith('972')) return `0${digits.slice(3)}`;
  return digits;
}
